import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
    Card,
    CardBody,
    Typography,
    Button,
    Chip,
    IconButton,
    Input,
    Select,
    Option,
    Menu,
    MenuHandler,
    MenuList,
    MenuItem
} from '@material-tailwind/react';
import {
    MagnifyingGlassIcon,
    EllipsisVerticalIcon,
    CheckCircleIcon,
    ClockIcon,
    ExclamationCircleIcon,
    UserIcon,
    CalendarIcon
} from '@heroicons/react/24/outline';
import ConfirmModal from '../../common/ConfirmModal';
import TaskFilterBar from './TaskFilterBar';
import {
    TASK_LIST_PAGE_SIZE,
    buildTaskListRequest,
    mergeTaskChunks
} from './taskListQuery';

const TaskList = ({
    projectId,
    embedded = false,
    filters: externalFilters,
    filterSetters: externalFilterSetters,
    reloadTrigger = 0,
    onCreateTask,
}) => {
    const { rootUrl } = require('../../../config/config');
    const navigate = useNavigate();
    const [token] = useState(localStorage.getItem('authToken') || '');

    // Internal filter state — used when embedded (WorkflowDetail) or no external filters
    const [internalScope, setInternalScope] = useState('all');
    const [internalStatus, setInternalStatus] = useState('all');
    const [internalPriority, setInternalPriority] = useState('all');
    const [internalSort, setInternalSort] = useState('created_desc');
    const [internalSearchInput, setInternalSearchInput] = useState('');
    const [internalDebouncedSearch, setInternalDebouncedSearch] = useState('');

    const scope = externalFilters?.scope ?? internalScope;
    const status = externalFilters?.status ?? internalStatus;
    const priority = externalFilters?.priority ?? internalPriority;
    const sort = externalFilters?.sort ?? internalSort;
    const search = externalFilters?.search ?? internalDebouncedSearch;
    const searchInput = externalFilterSetters?.searchInput ?? internalSearchInput;

    const setScope = externalFilterSetters?.setScope ?? setInternalScope;
    const setStatus = externalFilterSetters?.setStatus ?? setInternalStatus;
    const setPriority = externalFilterSetters?.setPriority ?? setInternalPriority;
    const setSort = externalFilterSetters?.setSort ?? setInternalSort;
    const setSearchInput = externalFilterSetters?.setSearchInput ?? setInternalSearchInput;
    const clearFilters = externalFilterSetters?.clearFilters ?? (() => {
        setInternalScope('all');
        setInternalStatus('all');
        setInternalPriority('all');
        setInternalSort('created_desc');
        setInternalSearchInput('');
    });
    const hasActiveFilters = externalFilterSetters?.hasActiveFilters
        ?? (scope !== 'all' || status !== 'all' || priority !== 'all' || search !== '');

    const [tasks, setTasks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [offset, setOffset] = useState(0);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [deleteError, setDeleteError] = useState('');

    const loadMoreAnchorRef = useRef(null);
    const requestInFlightRef = useRef(false);
    const hasMoreRef = useRef(true);
    const requestSequenceRef = useRef(0);
    const internalDebounceRef = useRef(null);

    useEffect(() => {
        hasMoreRef.current = hasMore;
    }, [hasMore]);

    useEffect(() => {
        if (externalFilters) return; // external mode — TasksPage handles debounce
        if (internalDebounceRef.current) clearTimeout(internalDebounceRef.current);
        internalDebounceRef.current = setTimeout(() => {
            setInternalDebouncedSearch(internalSearchInput);
        }, 300);
        return () => clearTimeout(internalDebounceRef.current);
    }, [internalSearchInput, externalFilters]);

    const loadTasks = useCallback(async ({ reset = false, pageOffset = 0 } = {}) => {
        if (!reset && !hasMoreRef.current) return;
        if (!reset && requestInFlightRef.current) return;

        const requestId = requestSequenceRef.current + 1;
        requestSequenceRef.current = requestId;

        if (reset) {
            setLoading(true);
            setLoadingMore(false);
            setTasks([]);
            setOffset(0);
            setHasMore(true);
        } else {
            requestInFlightRef.current = true;
            setLoadingMore(true);
        }

        try {
            const { url, params } = buildTaskListRequest({
                rootUrl,
                projectId,
                scope,
                status,
                priority,
                search,
                sort,
                limit: TASK_LIST_PAGE_SIZE,
                offset: pageOffset,
            });

            const response = await axios.get(url, {
                params,
                headers: { Authorization: `Bearer ${token}` },
            });

            if (requestId !== requestSequenceRef.current) return;

            if (response.data.success) {
                const incoming = response.data.data || [];
                setTasks(prev => reset ? incoming : mergeTaskChunks(prev, incoming));
                const pagination = response.data.pagination || {};
                setOffset(pagination.next_offset ?? (pageOffset + incoming.length));
                setHasMore(Boolean(pagination.has_more));
            } else {
                if (reset) setTasks([]);
                setHasMore(false);
            }
        } catch (error) {
            if (requestId !== requestSequenceRef.current) return;
            console.error('Error fetching tasks:', error);
            if (reset) {
                setTasks([]);
                setHasMore(false);
            }
        } finally {
            if (!reset) requestInFlightRef.current = false;
            if (requestId === requestSequenceRef.current) {
                setLoading(false);
                setLoadingMore(false);
            }
        }
    }, [priority, projectId, rootUrl, scope, search, sort, status, token]);

    useEffect(() => {
        loadTasks({ reset: true, pageOffset: 0 });
    }, [loadTasks, reloadTrigger]);

    const handleLoadMore = useCallback(() => {
        if (!loadingMore && hasMore) loadTasks({ reset: false, pageOffset: offset });
    }, [hasMore, loadingMore, loadTasks, offset]);

    useEffect(() => {
        if (!loadMoreAnchorRef.current || loading || loadingMore || !hasMore) return;

        const observer = new IntersectionObserver(entries => {
            if (entries.some(e => e.isIntersecting)) handleLoadMore();
        }, { rootMargin: '200px' });

        observer.observe(loadMoreAnchorRef.current);
        return () => observer.disconnect();
    }, [handleLoadMore, hasMore, loading, loadingMore, tasks.length]);

    const handleDeleteConfirm = async () => {
        if (!deleteTarget) return;
        setDeleteLoading(true);
        setDeleteError('');
        try {
            const response = await axios.delete(
                `${rootUrl}/api/tasks/${deleteTarget.id}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            if (response.data.success) {
                setTasks(prev => prev.filter(t => t.id !== deleteTarget.id));
                setDeleteTarget(null);
            } else {
                setDeleteError(response.data.message || 'Failed to delete task');
            }
        } catch (error) {
            setDeleteError(error.response?.data?.message || 'Failed to delete task');
        } finally {
            setDeleteLoading(false);
        }
    };

    const getPriorityColor = (priority) => {
        const colors = { low: 'blue-gray', normal: 'blue', high: 'orange', urgent: 'red' };
        return colors[priority] || 'gray';
    };

    const getStatusColor = (status) => {
        const colors = {
            open: 'gray', 'To Do': 'gray', 'In Progress': 'blue',
            Review: 'orange', Done: 'green', Completed: 'green', Cancelled: 'red',
        };
        return colors[status] || 'gray';
    };

    const formatDate = (dateString) => {
        if (!dateString) return null;
        const date = new Date(dateString);
        const today = new Date();
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);
        if (date.toDateString() === today.toDateString()) return 'Today';
        if (date.toDateString() === tomorrow.toDateString()) return 'Tomorrow';
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    };

    const isOverdue = (dueDate) => dueDate && new Date(dueDate) < new Date();

    const filterBar = (
        <TaskFilterBar>
            <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_180px_180px_180px_180px]">
                <Input
                    icon={<MagnifyingGlassIcon className="h-5 w-5" />}
                    label="Search tasks..."
                    value={searchInput}
                    onChange={e => setSearchInput(e.target.value)}
                />
                <Select label="Scope" value={scope} onChange={setScope}>
                    <Option value="all">All Tasks</Option>
                    <Option value="assigned">Assigned to Me</Option>
                    <Option value="created">Created by Me</Option>
                </Select>
                <Select label="Status" value={status} onChange={setStatus}>
                    <Option value="all">All Status</Option>
                    <Option value="open">Open</Option>
                    <Option value="To Do">To Do</Option>
                    <Option value="In Progress">In Progress</Option>
                    <Option value="Review">Review</Option>
                    <Option value="Done">Done</Option>
                </Select>
                <Select label="Priority" value={priority} onChange={setPriority}>
                    <Option value="all">All Priority</Option>
                    <Option value="low">Low</Option>
                    <Option value="normal">Normal</Option>
                    <Option value="high">High</Option>
                    <Option value="urgent">Urgent</Option>
                </Select>
                <Select label="Sort by" value={sort} onChange={setSort}>
                    <Option value="created_desc">Created (newest)</Option>
                    <Option value="due_asc">Due date (soonest)</Option>
                    <Option value="priority_desc">Priority (highest)</Option>
                </Select>
            </div>
        </TaskFilterBar>
    );

    const emptyState = (
        <div className="text-center py-12 space-y-3">
            {hasActiveFilters ? (
                <>
                    <Typography color="gray">No tasks match your filters.</Typography>
                    <button
                        type="button"
                        onClick={clearFilters}
                        className="text-sm text-[#FF5733] hover:underline"
                    >
                        Clear filters
                    </button>
                </>
            ) : (
                <>
                    <Typography color="gray">No tasks yet.</Typography>
                    {onCreateTask && (
                        <Button
                            onClick={onCreateTask}
                            className="bg-[#FF5733] hover:bg-[#E64A2E]"
                            size="sm"
                        >
                            Create your first task
                        </Button>
                    )}
                </>
            )}
        </div>
    );

    const taskListContent = (
        <>
            {loading ? (
                <Card className="shadow-sm border border-white/70">
                    <CardBody className="text-center py-12">
                        <Typography color="gray">Loading tasks...</Typography>
                    </CardBody>
                </Card>
            ) : (
                <Card className="shadow-sm border border-white/70">
                    <CardBody>
                        {tasks.length === 0 ? emptyState : (
                            <div className="space-y-3">
                                {tasks.map(task => (
                                    <div key={task.id} className="relative group">
                                        <Card
                                            className="hover:shadow-lg transition-shadow cursor-pointer"
                                            onClick={() => navigate(`/tasks/${task.id}`)}
                                        >
                                            <CardBody className="p-4 pr-12">
                                                <div className="flex-1">
                                                    <div className="flex items-center gap-3 mb-2">
                                                        <Typography
                                                            variant="h6"
                                                            color="blue-gray"
                                                            className="hover:text-[#FF5733]"
                                                        >
                                                            {task.title}
                                                        </Typography>
                                                        <Chip
                                                            size="sm"
                                                            value={task.priority}
                                                            color={getPriorityColor(task.priority)}
                                                            className="capitalize"
                                                        />
                                                    </div>

                                                    {task.description && (
                                                        <Typography
                                                            variant="small"
                                                            color="gray"
                                                            className="mb-3 line-clamp-2"
                                                        >
                                                            {task.description}
                                                        </Typography>
                                                    )}

                                                    <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
                                                        <Chip
                                                            size="sm"
                                                            value={task.status_name || task.status}
                                                            color={getStatusColor(task.status_name || task.status)}
                                                            className="capitalize"
                                                        />

                                                        {task.project_name && (
                                                            <span className="text-xs text-gray-400">
                                                                {task.project_name}
                                                            </span>
                                                        )}

                                                        {task.due_date && (
                                                            <div className={`flex items-center gap-1 ${isOverdue(task.due_date) ? 'text-red-500' : ''}`}>
                                                                <CalendarIcon className="h-4 w-4" />
                                                                <span>{formatDate(task.due_date)}</span>
                                                                {isOverdue(task.due_date) && (
                                                                    <ExclamationCircleIcon className="h-4 w-4" />
                                                                )}
                                                            </div>
                                                        )}

                                                        {task.assigned_to && task.assigned_to.length > 0 && (
                                                            <div className="flex items-center gap-1">
                                                                <UserIcon className="h-4 w-4" />
                                                                <span>{task.assigned_to.length} assigned</span>
                                                            </div>
                                                        )}

                                                        {task.total_checklist_items > 0 && (
                                                            <div className="flex items-center gap-1">
                                                                <CheckCircleIcon className="h-4 w-4" />
                                                                <span>
                                                                    {task.completed_checklist_items}/{task.total_checklist_items}
                                                                </span>
                                                            </div>
                                                        )}

                                                        {task.estimated_hours && (
                                                            <div className="flex items-center gap-1">
                                                                <ClockIcon className="h-4 w-4" />
                                                                <span>{task.estimated_hours}h estimated</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </CardBody>
                                        </Card>

                                        <div className="absolute top-2 right-2">
                                            <Menu>
                                                <MenuHandler>
                                                    <IconButton variant="text" size="sm">
                                                        <EllipsisVerticalIcon className="h-5 w-5" />
                                                    </IconButton>
                                                </MenuHandler>
                                                <MenuList>
                                                    <MenuItem onClick={() => navigate(`/tasks/${task.id}`)}>
                                                        Edit
                                                    </MenuItem>
                                                    <MenuItem
                                                        className="text-red-500"
                                                        onClick={() => { setDeleteTarget(task); setDeleteError(''); }}
                                                    >
                                                        Delete
                                                    </MenuItem>
                                                </MenuList>
                                            </Menu>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        <div ref={loadMoreAnchorRef} className="py-6 flex flex-col items-center gap-3">
                            {loadingMore && (
                                <Typography color="gray">Loading more tasks...</Typography>
                            )}
                            {!loadingMore && hasMore && (
                                <Button
                                    variant="outlined"
                                    className="border-[#FF5733] text-[#FF5733]"
                                    onClick={handleLoadMore}
                                >
                                    Load more
                                </Button>
                            )}
                        </div>
                    </CardBody>
                </Card>
            )}

            <ConfirmModal
                isOpen={!!deleteTarget}
                title="Delete task"
                description={deleteTarget ? `Permanently delete "${deleteTarget.title}"? This cannot be undone.` : ''}
                onCancel={() => { setDeleteTarget(null); setDeleteError(''); }}
                onConfirm={handleDeleteConfirm}
                confirmText="Delete"
                cancelText="Cancel"
                loading={deleteLoading}
                error={deleteError}
                variant="danger"
            />
        </>
    );

    return (
        <div className="space-y-4">
            {filterBar}
            {taskListContent}
        </div>
    );
};

export default TaskList;
